import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUpload, faHandshake, faBoxOpen, faBoxesStacked, faChartLine, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import StockItemList from './StockItemList';
import StockItemForm from './StockItemForm';
import StockItemVariantForm from './StockItemVariantForm';
import ContractUploadModal from './ContractUploadModal';
import StockItemsSearch from './StockItemsSearch';
import ServicesTab from './ServicesTab';
import StockpileDashboard from './Stockpile/StockpileDashboard';
import { useFlashMessage } from './FlashMessage';
import axiosInstance from '../utils/axiosConfig';
import Footer from './ui/Footer';
import HMSPageHeader from './ui/HMSPageHeader';
import HamsterFloatingButton from './HamsterFloatingButton';
import BackToTopButton from './BackToTopButton';
import ProductCsvImportModal from './Stock/ProductCsvImportModal';
import SupplierListUploadModal from './Stock/SupplierListUploadModal';
import { CATEGORIES, isBrandedCategory } from '../data/productTaxonomy';
import './StockModule.css';

const DEFAULT_CATEGORY = 'coal';
const ALL_SUBTYPES = '__all__';
const SERVICES_VIEW = 'services';
// The list arrives 50 at a time; "Load 50 more" asks for the next page.
const PAGE_SIZE = 50;

// Pill tile tint per product category. The tile paints one 3-stop gradient
// from a single colour, so each category keeps the readable stop of the
// two-stop gradient its old tab tile carried (productTaxonomy `gradient`).
const CATEGORY_TINTS = {
    coal: '#5C258D',
    oil_gas: '#FF6B6B',
    minerals: '#4A6FA5',
    metals: '#757F9A',
    cementitious: '#2C3E50',
    biomass: '#56AB2F',
    other: '#8360C3',
};

const StockModule = () => {
    const [viewMode, setViewMode] = useState('catalog');
    const [activeCategory, setActiveCategory] = useState(DEFAULT_CATEGORY);
    const [activeSubtype, setActiveSubtype] = useState(ALL_SUBTYPES);
    // For the Other category we let the user toggle between products and services
    const [otherView, setOtherView] = useState('products');
    const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
    // Lubes come in through HMS's own complete list, not the generic CSV door,
    // because only that door can retire what HMS dropped (2026-09-21).
    const [isSupplierListOpen, setIsSupplierListOpen] = useState(false);
    // Retired lubes are hidden from the list by default so the tab matches
    // HMS's latest list; this shows them (read-only history).
    const [showRetired, setShowRetired] = useState(false);
    // Per-category counts from /categories/, including how many products are
    // still missing GL coding. Lubrication is exempt from the creation gate so
    // a bulk-loaded range arrives uncoded on purpose -- this badge is what stops
    // that being invisible, the way the IFRS mapping columns went live and sat
    // at 0 of 338 mapped because nothing ever showed the gap.
    const [categoryStats, setCategoryStats] = useState({});
    const [statsRefreshKey, setStatsRefreshKey] = useState(0);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isVariantFormOpen, setIsVariantFormOpen] = useState(false);
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [stockItems, setStockItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // How many products exist for this tab (the server's count, never the
    // number drawn) and how many pages we hold, so the screen can always say
    // "Showing X of Y" and a refresh keeps the rows the user already opened.
    const [totalCount, setTotalCount] = useState(0);
    const [pagesLoaded, setPagesLoaded] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);

    const [searchResults, setSearchResults] = useState([]);
    const [activeFilters, setActiveFilters] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);

    const [contractExtractionData, setContractExtractionData] = useState(null);
    const [createdStockItemId, setCreatedStockItemId] = useState(null);
    // The product typed in step 1, held in memory until the first item is
    // submitted with it (one atomic POST). Nothing is written until then, so
    // abandoning the flow can no longer leave a product with no items — which
    // the module listed as a real product and no picker could ever find
    // (invoice-product-search-silent-drops, 2026-08-19).
    const [pendingStockItem, setPendingStockItem] = useState(null);
    // Track the just-created stock item's product_type so the variant form can
    // render the correct subtype-conditional sections (e.g. Mixed Fuels Spec).
    // We can't use `activeSubtype` here because that's the filter chip in the
    // sidebar — when the user is on the "All" chip, or picks a different
    // subtype in the create form, `activeSubtype` doesn't reflect the actual
    // product they just created.
    const [createdStockItemSubtype, setCreatedStockItemSubtype] = useState(null);

    const { showSuccess, showError } = useFlashMessage();

    const activeCategoryDef = CATEGORIES.find((c) => c.value === activeCategory) || CATEGORIES[0];

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get('/api/stock/stock-items/categories/');
                if (cancelled) return;
                const byValue = {};
                (res.data || []).forEach((c) => { byValue[c.value] = c; });
                setCategoryStats(byValue);
            } catch (e) {
                // A missing badge is a cosmetic loss; it must never blank the tabs.
                if (!cancelled) setCategoryStats({});
            }
        })();
        return () => { cancelled = true; };
    }, [statsRefreshKey]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // When switching category, reset the subtype chip selection
    useEffect(() => {
        setActiveSubtype(ALL_SUBTYPES);
    }, [activeCategory]);

    useEffect(() => {
        if (!isSearchMode && !(activeCategory === 'other' && otherView === SERVICES_VIEW)) {
            loadStockItems();
        }
    }, [activeCategory, activeSubtype, otherView, isSearchMode, showRetired]);

    // Every list fetch is stamped, and only the newest one is allowed to write.
    // Without this the slowest response wins whichever tab you have ended up on:
    // switching to Lubrication (921 rows) while the Coal request was still in
    // flight left COAL products sitting under the Lubrication heading, tab
    // highlighted and all. Caught on camera during verification, 2026-09-03.
    const listRequestId = useRef(0);

    const fetchPage = (page) => {
        const params = new URLSearchParams();
        params.append('category', activeCategory);
        if (activeSubtype && activeSubtype !== ALL_SUBTYPES) {
            params.append('product_type', activeSubtype);
        }
        if (showRetired && isBrandedCategory(activeCategory)) {
            params.append('retired', 'only');
        }
        params.append('page', page);
        params.append('page_size', PAGE_SIZE);
        return axiosInstance.get(`/api/stock/stock-items/?${params.toString()}`);
    };

    // Opening a tab loads the first 50. A refresh (after an item is edited
    // inside an open product) reloads as many pages as were already showing,
    // so someone 300 rows down is not thrown back to the top 50.
    const loadStockItems = async ({ keepDepth = false } = {}) => {
        const requestId = ++listRequestId.current;
        const pageCount = keepDepth ? Math.max(1, pagesLoaded) : 1;
        setLoading(true);
        try {
            const responses = await Promise.all(
                Array.from({ length: pageCount }, (_, i) => fetchPage(i + 1))
            );
            // A stale response is DROPPED, not rendered. Showing another
            // category's products is worse than showing none.
            if (requestId !== listRequestId.current) return;
            const rows = responses.flatMap((r) => r.data.results || []);
            setStockItems(rows);
            setTotalCount(responses[0].data.count ?? rows.length);
            setPagesLoaded(pageCount);
        } catch (error) {
            if (requestId !== listRequestId.current) return;
            console.error('Failed to load stock items:', error);
            showError('Failed to load stock items');
        } finally {
            if (requestId === listRequestId.current) setLoading(false);
        }
    };

    const loadMoreStockItems = async () => {
        // Shares the stamp with loadStockItems: switching tab while the next
        // 50 are in flight drops them instead of appending another tab's rows.
        const requestId = ++listRequestId.current;
        const nextPage = pagesLoaded + 1;
        setLoadingMore(true);
        try {
            const response = await fetchPage(nextPage);
            if (requestId !== listRequestId.current) return;
            const incoming = response.data.results || [];
            // A product edited since page 1 moves to the top and could appear
            // on the next page too; never draw the same product twice.
            setStockItems((prev) => {
                const seen = new Set(prev.map((i) => i.id));
                return [...prev, ...incoming.filter((i) => !seen.has(i.id))];
            });
            setTotalCount(response.data.count ?? totalCount);
            setPagesLoaded(nextPage);
        } catch (error) {
            if (requestId !== listRequestId.current) return;
            console.error('Failed to load more stock items:', error);
            showError('Could not load more products. Please try again.');
        } finally {
            if (requestId === listRequestId.current) setLoadingMore(false);
        }
    };

    const handleStockItemCreated = (payload) => {
        // Deferred mode: StockItemForm hands over the validated product, it has
        // written NOTHING. The item step submits product + item together.
        console.log('🎯 Product step complete, holding draft until the first item:', payload);
        setIsFormOpen(false);
        setPendingStockItem(payload.draft);
        setCreatedStockItemId(null);
        setCreatedStockItemSubtype(payload.product_type || null);
        setIsVariantFormOpen(true);
        showSuccess(
            contractExtractionData
                ? 'Product details captured — now add its first item with the contract data.'
                : 'Product details captured — now add its first item to finish.'
        );
    };

    // "Use this one instead" on the duplicate warning. Nothing new is written:
    // the existing product's item step opens instead, so the trader adds their
    // item to the product that already exists rather than to a second copy.
    const handleUseExistingProduct = (match) => {
        if (!match || !match.id) return;
        setIsFormOpen(false);
        setPendingStockItem(null);
        setCreatedStockItemId(match.id);
        setCreatedStockItemSubtype(match.product_type || null);
        setIsVariantFormOpen(true);
        showSuccess(`Using the existing product "${match.display_name}" — add your item to it.`);
    };

    const handleVariantCreated = () => {
        const wasNewProduct = !!pendingStockItem;
        setIsVariantFormOpen(false);
        setContractExtractionData(null); // Clear the extraction data
        setCreatedStockItemId(null); // Clear the stock item ID
        setCreatedStockItemSubtype(null);
        setPendingStockItem(null);
        
        if (isSearchMode) {
            // If in search mode, refresh search results
            // This will be handled by the search component automatically
        } else {
            loadStockItems();
        }
        showSuccess(wasNewProduct
            ? 'Product created with its first item — it can now be picked on invoices, deliveries and recaps.'
            : 'Item added successfully.');
    };

    const handleContractUploaded = (data) => {
        console.log('📋 Contract extracted data received:', data);
        
        if (data && data.selectedData) {
            // Store the extracted data and keep the modal open for stock item creation
            setContractExtractionData(data);
            setIsFormOpen(true); // Open stock item form with pre-filled data
            setIsContractModalOpen(false); // Close contract modal
        } else {
            // Fallback for manual mode
            setIsContractModalOpen(false);
            if (isSearchMode) {
                // If in search mode, refresh search results
                // This will be handled by the search component automatically
            } else {
                loadStockItems();
            }
            showSuccess('Contract uploaded successfully');
        }
    };

    const handleSearchResults = (results) => {
        console.log('🔥 handleSearchResults() called with', results.length, 'results');
        
        // Always accept results - the search component handles timing appropriately
        
        // Transform search results to match the expected stock item format
        const transformedResults = results.map(result => {
            if (result.type === 'stock_item' || result.type === 'stock_variant') {
                // Extract the base item data and add search-specific fields
                return {
                    ...result.base_item,
                    // Add search metadata for potential future use
                    _searchType: result.type,
                    _searchRelevance: result.relevance_score,
                    _searchVariant: result.variant,
                    // Use display_name from search if available, otherwise use dynamic_name
                    display_name: result.display_name || result.base_item.dynamic_name
                };
            }
            return result;
        });
        
        setSearchResults(transformedResults);
        
        // Manage search mode based on results
        if (transformedResults.length > 0) {
            // Enter search mode when we have results
            console.log('🔥 ENTERING SEARCH MODE with', transformedResults.length, 'results');
            setIsSearchMode(true);
        } else {
            // Exit search mode when we have no results (cleared search)
            console.log('🔥 EXITING SEARCH MODE - NO RESULTS, LOADING ALL ITEMS');
            setIsSearchMode(false);
            // Reload all stock items when no search results
            loadStockItems();
        }
    };

    const handleFiltersChange = (filters) => {
        setActiveFilters(filters);
        // Update search mode based on filters
        const hasFilters = Object.keys(filters).some(key => filters[key]);
        if (hasFilters) {
            setIsSearchMode(true);
        }
    };

    const handleSearchStateChange = (searching) => {
        setIsSearching(searching);
        // Don't change search mode here - let handleSearchResults manage it
    };

    const handleClearSearch = () => {
        console.log('🔥 handleClearSearch() CALLED - EXITING SEARCH MODE AND LOADING ALL ITEMS');
        setIsSearchMode(false);
        setSearchResults([]);
        setActiveFilters({});
        // Reload all stock items when search is cleared
        loadStockItems();
    };

    // Tab tray for the shared page header: one pill per product category, no
    // counts (the old tabs showed none either). The product tour anchored on
    // the tab strip, which the shared header owns now, so the anchor rides the
    // first pill.
    const categoryTabs = CATEGORIES.map((cat, index) => ({
        key: cat.value,
        label: cat.label,
        icon: cat.icon,
        tint: CATEGORY_TINTS[cat.value],
        dataTour: index === 0 ? 'product-category-tabs' : undefined,
    }));

    const renderTabContent = () => {
        const itemsToShow = isSearchMode ? searchResults : stockItems;
        const loadingState = isSearchMode ? isSearching : loading;

        const showingServices = activeCategory === 'other' && otherView === SERVICES_VIEW;
        if (showingServices) {
            return <ServicesTab />;
        }

        return (
            <div className="all-items-content">
                <StockItemsSearch
                    onSearchResults={handleSearchResults}
                    onFiltersChange={handleFiltersChange}
                    isSearching={isSearching}
                    onSearchStateChange={handleSearchStateChange}
                    onClearSearch={handleClearSearch}
                    activeCategory={activeCategory}
                    activeSubtype={activeSubtype === ALL_SUBTYPES ? null : activeSubtype}
                />

                <StockItemList
                    items={itemsToShow}
                    loading={loadingState}
                    onRefresh={isSearchMode ? () => {
                        console.log('Refresh in search mode - handled by search component');
                    } : () => loadStockItems({ keepDepth: true })}
                    productType={activeSubtype === ALL_SUBTYPES ? null : activeSubtype}
                    category={activeCategory}
                    onAddFirstItem={() => setIsFormOpen(true)}
                    isSearchMode={isSearchMode}
                    searchResultsCount={isSearchMode ? searchResults.length : null}
                    totalCount={isSearchMode ? null : totalCount}
                    pageSize={PAGE_SIZE}
                    onLoadMore={loadMoreStockItems}
                    loadingMore={loadingMore}
                />
            </div>
        );
    };

    return (
        <div className="stock-module" data-tour="product-page">
            {/* Shared screen header: brand block, category pill tray, view
                toggle + action buttons. The same category/subtype filters
                drive both Catalog and Stockpile modes, so the user keeps a
                consistent mental model. */}
            <HMSPageHeader
                title="Product Management"
                kicker="Operations"
                tabs={{ items: categoryTabs, activeKey: activeCategory, onChange: setActiveCategory, dataTour: 'product-category-tabs' }}
                actions={
                    <>
                        <div className="stock-view-toggle-inline" role="tablist" aria-label="View mode" data-tour="product-view-toggle">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={viewMode === 'catalog'}
                                className={`stock-view-pill ${viewMode === 'catalog' ? 'active' : ''}`}
                                onClick={() => setViewMode('catalog')}
                                title="Browse the product catalog"
                                data-tour="product-view-catalog"
                            >
                                <FontAwesomeIcon icon={faBoxesStacked} />
                                <span>Catalog</span>
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={viewMode === 'stockpile'}
                                className={`stock-view-pill ${viewMode === 'stockpile' ? 'active' : ''}`}
                                onClick={() => setViewMode('stockpile')}
                                title="See live stockpile balances and history"
                                data-tour="product-view-stockpile"
                            >
                                <FontAwesomeIcon icon={faChartLine} />
                                <span>Stockpile</span>
                            </button>
                        </div>
                        <div className="header-actions" data-tour="product-header-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setIsContractModalOpen(true)}
                                data-tour="product-upload-contract"
                            >
                                <FontAwesomeIcon icon={faUpload} size="sm" />
                                Upload Contract
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={() => setIsFormOpen(true)}
                                data-tour="product-add-item"
                            >
                                <FontAwesomeIcon icon={faPlus} size="sm" />
                                Add Product Item
                            </button>
                        </div>
                    </>
                }
            />

            {/* Subtype chip-row (or Products/Services toggle for Other) */}
            {/* Branded categories have no sub-categories (HMS's ask), so the
              * chip row is suppressed rather than rendered with a single chip
              * that does nothing. */}
            {isBrandedCategory(activeCategory) && viewMode === 'catalog' ? (
                <div className="stock-subtype-chips">
                    <button type="button" className="subtype-chip active" disabled>
                        All {activeCategoryDef.label}
                    </button>
                    {(categoryStats[activeCategory]?.needs_gl_coding > 0) && (
                        <span className="stock-coding-gap-badge">
                            {categoryStats[activeCategory].needs_gl_coding} product(s) still need GL coding
                        </span>
                    )}
                    <button
                        type="button"
                        className={`subtype-chip ${showRetired ? 'active' : ''}`}
                        onClick={() => setShowRetired((v) => !v)}
                        title="Lubes that dropped off IMT's list. They stay on past deals but cannot be picked for new ones."
                    >
                        {showRetired ? 'Showing retired lubes' : 'Show retired lubes'}
                    </button>
                    <button
                        type="button"
                        className="subtype-chip stock-csv-import-chip"
                        onClick={() => setIsSupplierListOpen(true)}
                    >
                        <FontAwesomeIcon icon={faFileCsv} /> Upload IMT lube list
                    </button>
                </div>
            ) : activeCategory === 'other' && viewMode === 'catalog' ? (
                <div className="stock-other-toggle-row">
                    <button
                        type="button"
                        className={`stock-other-toggle ${otherView === 'products' ? 'active' : ''}`}
                        onClick={() => setOtherView('products')}
                    >
                        <FontAwesomeIcon icon={faBoxOpen} />
                        <span>Products</span>
                    </button>
                    <button
                        type="button"
                        className={`stock-other-toggle ${otherView === SERVICES_VIEW ? 'active' : ''}`}
                        onClick={() => setOtherView(SERVICES_VIEW)}
                    >
                        <FontAwesomeIcon icon={faHandshake} />
                        <span>Services</span>
                    </button>
                </div>
            ) : (
                <div className="stock-subtype-chips" data-tour="product-subtype-chips">
                    <button
                        type="button"
                        className={`subtype-chip ${activeSubtype === ALL_SUBTYPES ? 'active' : ''}`}
                        onClick={() => setActiveSubtype(ALL_SUBTYPES)}
                    >
                        All {activeCategoryDef.label}
                    </button>
                    {activeCategoryDef.subtypes.map((sub) => (
                        <button
                            key={sub.value}
                            type="button"
                            className={`subtype-chip ${activeSubtype === sub.value ? 'active' : ''}`}
                            onClick={() => setActiveSubtype(sub.value)}
                        >
                            {sub.label}
                        </button>
                    ))}
                    {viewMode === 'catalog' && (
                        <button
                            type="button"
                            className="subtype-chip stock-csv-import-chip"
                            onClick={() => setIsCsvImportOpen(true)}
                        >
                            <FontAwesomeIcon icon={faFileCsv} /> Import from CSV
                        </button>
                    )}
                </div>
            )}

            {viewMode === 'stockpile' ? (
                <div className="stock-content" data-tour="product-content">
                    <StockpileDashboard
                        category={activeCategory}
                        productType={activeSubtype === ALL_SUBTYPES ? null : activeSubtype}
                    />
                </div>
            ) : (
                <div className="stock-content" data-tour="product-content">
                    {renderTabContent()}
                </div>
            )}

            {/* Modals */}
            <SupplierListUploadModal
                isOpen={isSupplierListOpen}
                onClose={() => setIsSupplierListOpen(false)}
                onApplied={(result) => {
                    showSuccess(result.message);
                    setIsSupplierListOpen(false);
                    loadStockItems();
                    setStatsRefreshKey((k) => k + 1);
                }}
            />
            <ProductCsvImportModal
                isOpen={isCsvImportOpen}
                initialFormat="bulk_commodity"
                onClose={() => setIsCsvImportOpen(false)}
                onImported={(result) => {
                    showSuccess(result.message || 'Products imported.');
                    setIsCsvImportOpen(false);
                    // Refresh both the list and the GL-coding badge -- an import
                    // of uncoded lubes moves that number by hundreds.
                    loadStockItems();
                    setStatsRefreshKey((k) => k + 1);
                }}
            />

            {isFormOpen && (
                <StockItemForm
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        if (contractExtractionData) {
                            setContractExtractionData(null);
                        }
                    }}
                    onSuccess={handleStockItemCreated}
                    onUseExisting={handleUseExistingProduct}
                    deferSubmit
                    initialCategory={activeCategory}
                    initialProductType={activeSubtype === ALL_SUBTYPES ? null : activeSubtype}
                    preFilledData={contractExtractionData?.selectedData}
                    analysisId={contractExtractionData?.analysisId}
                />
            )}

            {isVariantFormOpen && (createdStockItemId || pendingStockItem) && (
                <StockItemVariantForm
                    isOpen={isVariantFormOpen}
                    onClose={() => {
                        setIsVariantFormOpen(false);
                        setCreatedStockItemSubtype(null);
                        // Abandoning here discards the held product outright —
                        // nothing was written, so nothing is left behind.
                        setPendingStockItem(null);
                        // Clean up contract extraction data when closing variant form
                        if (contractExtractionData) {
                            setContractExtractionData(null);
                            setCreatedStockItemId(null);
                        }
                    }}
                    onSuccess={handleVariantCreated}
                    stockItemId={createdStockItemId}
                    pendingStockItem={pendingStockItem}
                    parentSubtype={createdStockItemSubtype}
                    preFilledData={contractExtractionData?.selectedData}
                    analysisId={contractExtractionData?.analysisId}
                />
            )}

            {isContractModalOpen && (
                <ContractUploadModal
                    isOpen={isContractModalOpen}
                    onClose={() => setIsContractModalOpen(false)}
                    onSuccess={handleContractUploaded}
                />
            )}

            {/* Hamster Chat Integration */}
            <HamsterFloatingButton />

            {/* Back to Top Button */}
            <BackToTopButton />

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default StockModule; 